import React, { useState } from 'react';
import {
  X, Palette, Phone, Mic, Headphones, Bot, User, Eye, CheckCircle,
} from 'lucide-react';
import api from '../api';
import Toast from './Toast';

const COLOR_PRESETS = [
  { name: 'Indigo',  value: '#6366f1' },
  { name: 'Blue',    value: '#3b82f6' },
  { name: 'Purple',  value: '#a855f7' },
  { name: 'Pink',    value: '#ec4899' },
  { name: 'Red',     value: '#ef4444' },
  { name: 'Orange',  value: '#f97316' },
  { name: 'Green',   value: '#10b981' },
  { name: 'Teal',    value: '#14b8a6' },
  { name: 'Cyan',    value: '#06b6d4' },
  { name: 'Dark',    value: '#1e293b' },
];

const ICON_OPTIONS = [
  { name: 'Phone',      value: 'phone',      Icon: Phone },
  { name: 'Mic',        value: 'mic',        Icon: Mic },
  { name: 'Headphones', value: 'headphones', Icon: Headphones },
  { name: 'Bot',        value: 'bot',        Icon: Bot },
  { name: 'User',       value: 'user',       Icon: User },
];

const POSITION_OPTIONS = [
  { name: 'Bottom Right', value: 'bottom-right' },
  { name: 'Bottom Left',  value: 'bottom-left' },
  { name: 'Top Right',    value: 'top-right' },
  { name: 'Top Left',     value: 'top-left' },
];

// ─── Color swatch + picker ────────────────────────────────────────────────────

const ColorPicker = ({ label, fieldName, value, onChange, pickerId }) => (
  <div className="space-y-3">
    <label className="text-sm font-semibold text-foreground">{label}</label>
    <div className="grid grid-cols-5 gap-2">
      {COLOR_PRESETS.map((c) => (
        <button
          key={c.value}
          onClick={() => onChange(fieldName, c.value)}
          title={c.name}
          className={`flex flex-col items-center gap-1.5 p-2.5 rounded-lg border-2 transition-all ${
            value === c.value
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50'
          }`}
        >
          <div className="w-7 h-7 rounded-full" style={{ backgroundColor: c.value }} />
          <span className="text-[10px] text-foreground">{c.name}</span>
          {value === c.value && <CheckCircle className="w-3 h-3 text-primary" />}
        </button>
      ))}
    </div>
    {/* Custom color row */}
    <div className="bg-muted/30 border border-border/60 rounded-xl p-4 flex items-center gap-4">
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Picker</span>
        <label htmlFor={pickerId} className="relative cursor-pointer group">
          <div
            className="w-16 h-10 rounded-lg border-2 border-white shadow-md group-hover:scale-105 transition-transform ring-2 ring-offset-1 ring-border group-hover:ring-primary"
            style={{ backgroundColor: value }}
          />
          <input
            id={pickerId}
            type="color"
            value={value}
            onChange={(e) => onChange(fieldName, e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </label>
      </div>
      <div className="flex-1">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">Hex</span>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-sm pointer-events-none">#</span>
          <input
            type="text"
            value={value.replace('#', '')}
            onChange={(e) => {
              const v = '#' + e.target.value.replace('#', '');
              if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(fieldName, v);
            }}
            className="w-full pl-7 pr-3 py-2 text-sm font-mono font-semibold bg-background border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary uppercase"
            maxLength={6}
          />
        </div>
      </div>
    </div>
  </div>
);

// ─── Toggle switch ────────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange, label }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
        checked ? 'bg-primary' : 'bg-muted'
      }`}
    >
      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`} />
    </button>
    <span className="text-sm text-muted-foreground">{label}</span>
  </div>
);

// ─── Main customizer ──────────────────────────────────────────────────────────

const VoiceWidgetThemeCustomizer = ({ agent, onClose, onSave }) => {
  const [settings, setSettings] = useState({
    voice_widget_theme_mode:    agent?.voice_widget_theme_mode    || 'light',
    voice_widget_primary_color: agent?.voice_widget_primary_color || '#6366f1',
    voice_widget_button_color:  agent?.voice_widget_button_color  || '#6366f1',
    voice_widget_avatar_color:  agent?.voice_widget_avatar_color  || '#6366f1',
    voice_widget_button_position: agent?.voice_widget_button_position || 'bottom-right',
    voice_widget_icon:          agent?.voice_widget_icon          || 'phone',
    voice_widget_button_label:  agent?.voice_widget_button_label  || 'Talk to us',
    voice_widget_show_label:    agent?.voice_widget_show_label    ?? true,
    voice_widget_title:         agent?.voice_widget_title         || agent?.name || 'Voice Assistant',
    voice_widget_subtitle:      agent?.voice_widget_subtitle      || 'Click the button below to start talking',
    voice_widget_pulse_animation: agent?.voice_widget_pulse_animation ?? true,
    voice_widget_avatar_type:   agent?.voice_widget_avatar_type   || 'icon',
    voice_widget_welcome_message: agent?.voice_widget_welcome_message || '',
    voice_widget_show_transcript: agent?.voice_widget_show_transcript ?? true,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const set = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));
  const toggle = (field) => setSettings(prev => ({ ...prev, [field]: !prev[field] }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put(`/agents/${agent.id}`, settings);
      setToast({ message: 'Voice widget settings saved!', type: 'success' });
      setTimeout(() => {
        if (onSave) onSave(settings);
        onClose();
      }, 900);
    } catch {
      setToast({ message: 'Failed to save settings', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    const params = new URLSearchParams({ view: 'full' });
    window.open(`${window.location.origin}/embed/voice-widget/${agent.id}?${params}`, '_blank');
  };

  const SelectedIcon = ICON_OPTIONS.find(o => o.value === settings.voice_widget_icon)?.Icon || Phone;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">

        {/* Header */}
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <Phone className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Customize Voice Widget</h2>
              <p className="text-white/70 text-xs mt-0.5 font-normal">Configure your embeddable voice call button</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">

          {/* Theme mode */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Theme Mode</label>
            <div className="grid grid-cols-3 gap-3">
              {['light', 'dark', 'auto'].map(mode => (
                <button
                  key={mode}
                  onClick={() => set('voice_widget_theme_mode', mode)}
                  className={`px-4 py-3 rounded-lg border-2 transition-all capitalize text-sm ${
                    settings.voice_widget_theme_mode === mode
                      ? 'border-primary bg-primary/10 text-primary font-semibold'
                      : 'border-border hover:border-primary/50 text-foreground'
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          {/* Colors row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ColorPicker
              label="Header / Accent Color"
              fieldName="voice_widget_primary_color"
              value={settings.voice_widget_primary_color}
              onChange={set}
              pickerId="vw-primary-picker"
            />
            <ColorPicker
              label="Button Color"
              fieldName="voice_widget_button_color"
              value={settings.voice_widget_button_color}
              onChange={set}
              pickerId="vw-button-picker"
            />
            <ColorPicker
              label="Avatar Color"
              fieldName="voice_widget_avatar_color"
              value={settings.voice_widget_avatar_color}
              onChange={set}
              pickerId="vw-avatar-picker"
            />
          </div>

          {/* Icon */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Button Icon</label>
            <div className="grid grid-cols-5 gap-3">
              {ICON_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => set('voice_widget_icon', opt.value)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    settings.voice_widget_icon === opt.value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/50 text-foreground'
                  }`}
                >
                  <opt.Icon className="w-5 h-5" />
                  <span className="text-xs">{opt.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Position */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Button Position</label>
            <div className="grid grid-cols-2 gap-3">
              {POSITION_OPTIONS.map(p => (
                <button
                  key={p.value}
                  onClick={() => set('voice_widget_button_position', p.value)}
                  className={`px-4 py-3 rounded-lg border-2 transition-all text-sm ${
                    settings.voice_widget_button_position === p.value
                      ? 'border-primary bg-primary/10 text-primary font-semibold'
                      : 'border-border hover:border-primary/50 text-foreground'
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          {/* Avatar type */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Avatar Style (in call card)</label>
            <div className="grid grid-cols-2 gap-3">
              {[{ value: 'icon', label: 'Icon' }, { value: 'initials', label: 'Initials' }].map(at => (
                <button
                  key={at.value}
                  onClick={() => set('voice_widget_avatar_type', at.value)}
                  className={`px-4 py-3 rounded-lg border-2 transition-all text-sm ${
                    settings.voice_widget_avatar_type === at.value
                      ? 'border-primary bg-primary/10 text-primary font-semibold'
                      : 'border-border hover:border-primary/50 text-foreground'
                  }`}
                >
                  {at.label}
                </button>
              ))}
            </div>
          </div>

          {/* Text fields */}
          <div className="space-y-4">
            <label className="text-sm font-semibold text-foreground block">Labels and Text</label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Widget Title</label>
                <input
                  type="text"
                  value={settings.voice_widget_title}
                  onChange={e => set('voice_widget_title', e.target.value)}
                  placeholder="Voice Assistant"
                  className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Subtitle / Status Text</label>
                <input
                  type="text"
                  value={settings.voice_widget_subtitle}
                  onChange={e => set('voice_widget_subtitle', e.target.value)}
                  placeholder="Click to start talking"
                  className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground flex items-center gap-2">
                Launcher Button Label
                <span className="text-muted-foreground font-normal">(tooltip shown beside the button)</span>
              </label>
              <input
                type="text"
                value={settings.voice_widget_button_label}
                onChange={e => set('voice_widget_button_label', e.target.value)}
                placeholder="Talk to us"
                className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Welcome Message (shown before call starts)</label>
              <textarea
                value={settings.voice_widget_welcome_message}
                onChange={e => set('voice_widget_welcome_message', e.target.value)}
                placeholder="Hi there! Click the button below to start a voice conversation."
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-4">
            <label className="text-sm font-semibold text-foreground block">Options</label>
            <div className="space-y-3">
              <Toggle
                checked={settings.voice_widget_show_label}
                onChange={() => toggle('voice_widget_show_label')}
                label="Show label beside the launcher button"
              />
              <Toggle
                checked={settings.voice_widget_pulse_animation}
                onChange={() => toggle('voice_widget_pulse_animation')}
                label="Pulse animation on idle button"
              />
              <Toggle
                checked={settings.voice_widget_show_transcript}
                onChange={() => toggle('voice_widget_show_transcript')}
                label="Show live transcript in call card"
              />
            </div>
          </div>

          {/* Live preview */}
          <div className="bg-muted/30 rounded-xl p-5 border border-border space-y-3">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Preview
            </h3>
            <div className="flex items-end gap-6 p-4 bg-background rounded-lg min-h-[120px] relative">
              {/* Simulated launcher */}
              <div className="flex flex-col items-end gap-2 ml-auto">
                {settings.voice_widget_show_label && (
                  <div className="bg-card border border-border rounded-full px-3 py-1.5 text-xs font-semibold text-foreground shadow-sm whitespace-nowrap">
                    {settings.voice_widget_button_label || 'Talk to us'}
                  </div>
                )}
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                  style={{
                    backgroundColor: settings.voice_widget_button_color,
                    boxShadow: `0 4px 20px ${settings.voice_widget_button_color}66`,
                    animation: settings.voice_widget_pulse_animation ? 'none' : undefined,
                  }}
                >
                  <SelectedIcon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Click "Live Preview" to see the full widget in a new tab.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 bg-muted/30 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={handlePreview}
            className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors flex items-center gap-2 text-sm"
          >
            <Eye className="w-4 h-4" />
            Live Preview
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving…
              </>
            ) : 'Save Changes'}
          </button>
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default VoiceWidgetThemeCustomizer;
